<template>
  <div v-if="hasBingo" class="bingo-module pa-4">    
    <div class="bingo-module__header">
      <bingo-progress />
      <div class="bingo-filters" @click="showFiltersExpanded = !showFiltersExpanded">
        <div class="d-flex align-center justify-end gap-1">
          <!-- <icon-label-btn
            id="bingo_filters_btn"
            class="gtm-click"
            label="Filter"
            @click="showFiltersExpanded = !showFiltersExpanded"
          >
          mdi-filter-outline
          </icon-label-btn> -->
          <v-icon icon="mdi-filter-outline" size="24"></v-icon> Filter
        </div>        
      </div>      
    </div>

    <v-expand-transition>
      <div v-show="showFiltersExpanded" class="bingo-filters__panel rounded-lg mt-1 mb-2">
        <div class="bingo-filters__header d-flex align-center justify-space-between pa-4 pb-3">
          <span class="bingo-filters__title text-subtitle-1 font-weight-bold">Filters</span>
          <v-btn
            icon
            variant="flat"
            class="bingo-filters__close"
            @click="showFiltersExpanded = false"
          >
            <icon height="14" width="14">close-x</icon>
          </v-btn>
        </div>
        <div class="bingo-filters__body pa-4 pt-0">
          <template v-if="hasCategories">
            <div class="bingo-filters__label mb-2">CATEGORY</div>
            <v-chip-group
              v-model="bingoFilters.categoryId"
              class="bingo-filters__chip-group px-0 pt-0 pb-4"
              mandatory
              selected-class="bingo-filters__chip--selected"
            >
              <v-chip
                class="bingo-filters__chip font-weight-bold"
                :variant="bingoFilters.categoryId === null ? 'flat' : 'outlined'"
                :color="bingoFilters.categoryId === null ? 'primary' : undefined"
                :value="null"
              >
                All
              </v-chip>
              <v-chip
                v-for="category in categories"
                :key="category.id"
                class="bingo-filters__chip font-weight-bold"
                :variant="bingoFilters.categoryId === category.id ? 'flat' : 'outlined'"
                :color="bingoFilters.categoryId === category.id ? 'primary' : undefined"
                :value="category.id"
              >
                {{ category.name }}
              </v-chip>
            </v-chip-group>
          </template>
          <div class="bingo-filters__label mb-2">MAX DISTANCE</div>
          <v-chip-group
            v-model="bingoFilters.maxDistance"
            class="bingo-filters__chip-group px-0 pt-0"
            mandatory
            selected-class="bingo-filters__chip--selected"
          >
            <v-chip
              v-for="opt in maxDistanceOptions"
              :key="String(opt.value)"
              class="bingo-filters__chip font-weight-bold"
              :variant="bingoFilters.maxDistance === opt.value ? 'flat' : 'outlined'"
              :color="bingoFilters.maxDistance === opt.value ? 'primary' : undefined"
              :value="opt.value"
            >
              {{ opt.label }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </v-expand-transition>


    <bingo-label :label="bingoLabel" />
    <bingo-board
      :deal-id="bingoDealId"
      :is-cell-disabled="isCellDisabled"
      @click-cell="onCellClick"
    />
    <bingo-reward-cards
      :bingo-prizes="bingoPrizeLadder"
      :has-blackout="bingoHasBlackout"
      :blackout-prize-name="blackoutPrizeName"
      :blackout-unlocked="isBlackout"
    />
    <bingo-win-dialog
      v-model:open="showBingoWinDialog"
      :prizes="latestWonPrizesForDialog"
      :new-bingos="latestWonBingoNumbers.length"
      :continue-to-blackout="blackoutQueuedAfterBingo"
      @continue="onBingoWinContinue"
      @update:open="onBingoWinDialogUpdate"
    />
    <blackout-dialog
      v-model:open="showBlackoutDialog"
      :prize-name="blackoutPrizeName"
      @update:open="onBlackoutDialogUpdate"
    />
    <bingo-category-venues-modal
      v-model:open="showCategoryVenuesModal"
      :category-name="categoryModalData.categoryName"
      :category-id="categoryModalData.categoryId"
      :category-venue-ids="categoryModalData.categoryVenueIds"
      :deal-id="bingoDealId"
      :filters="bingoFiltersForModal"
      :used-venue-ids="usedVenueIds"
      :category-unavailable="categoryModalVenuesDisabled"
      :category-next-available-date="categoryModalNextAvailableDate"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { applySortDisplay, getVenueDistance } from '@/helpers';
import { mergeBingoCellQuery } from '@/utils/bingoCellUrl';
import IconLabelBtn from '@/components/shared/IconLabelBtn/index.vue';
import BingoBoard from './components/BingoBoard.vue';
import BingoLabel from './components/BingoLabel.vue';
import BingoProgress from './components/BingoProgress.vue';
import BingoRewardCards from './components/BingoRewardCards.vue';
import BingoWinDialog from './components/BingoWinDialog.vue';
import BlackoutDialog from './components/BlackoutDialog.vue';
import BingoCategoryVenuesModal from './components/BingoCategoryVenuesModal.vue';
import bingoModalSequence from '@/mixins/bingoModalSequence';

const MAX_DISTANCE_OPTIONS = [
  { label: 'Any', value: null },
  { label: '≤ 1340 mi', value: 1340 },
  { label: '≤ 1350 mi', value: 1350 },
  { label: '≤ 1355 mi', value: 1355 },
  { label: '≤ 1365 mi', value: 1365 }
];

export default {
  name: 'Bingo',
  mixins: [bingoModalSequence],
  components: {
    BingoBoard,
    BingoLabel,
    BingoProgress,
    BingoRewardCards,
    BingoWinDialog,
    BlackoutDialog,
    BingoCategoryVenuesModal,
    IconLabelBtn
  },
  props: {
    moduleData: {
      type: Object,
      default: () => ({})
    },
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      showCategoryVenuesModal: false,
      showFiltersExpanded: false,
      bingoFilters: {
        categoryId: null,
        maxDistance: null
      },
      maxDistanceOptions: MAX_DISTANCE_OPTIONS,
      categoryModalData: {
        categoryName: '',
        categoryId: null,
        categoryVenueIds: []
      }
    };
  },
  computed: {
    ...mapGetters('bingo', [
      'hasBingo',
      'pendingBingoCell',
      'usedVenueIds',
      'bingoRewardPrize',
      'blackoutPrize',
      'getCategoryAvailability',
      'latestWonBingoNumbers'
    ]),
    ...mapGetters('orderDeals', { orderDealsList: 'list' }),
    ...mapGetters('products', { productsByVenueId: 'byVenueId', getProductById: 'getById' }),
    ...mapGetters('venues', { findVenuesByDealId: 'findByDealId', findCategoriesByVenueIds: 'getCategoriesByVenueIds', findVenue: 'find' }),
    ...mapGetters('userLocation', { userLocation: 'getUserLocation' }),
    ...mapGetters('deals', { findDeal: 'find' }),
    ...mapState('bingo', [
      'lastCheckInResult',
      'boardMode',
      'label',
      'gridSize',
      'bingosAchieved',
      'isBlackout',
      'bingoPrizes'
    ]),
    bingoSettings() {
      const dealId = this.bingoDealId;
      if (dealId == null) return null;
      return this.findDeal(dealId)?.pass_settings?.bingo ?? null;
    },
    firstBingoPrizeName() {
      const productId = this.bingoSettings?.first_bingo_product_id;
      if (productId != null) {
        const product = this.getProductById(productId);
        if (product?.name) return product.name;
      }
      return this.bingoRewardPrize?.name ?? 'Prize';
    },
    bingoHasBlackout() {
      if (this.bingoSettings?.blackout_product_id != null) return true;
      return this.$store.getters['bingo/hasBlackoutPrize'];
    },
    blackoutPrizeName() {
      const productId = this.bingoSettings?.blackout_product_id;
      if (productId != null) {
        const product = this.getProductById(productId);
        if (product?.name) return product.name;
      }
      return this.blackoutPrize?.name ?? 'Prize';
    },
    bingoPrizeLadder() {
      const fromBackend = Array.isArray(this.bingoPrizes) ? this.bingoPrizes : [];
      let ladder = fromBackend
        .map(p => ({
          bingo_number: Number(p.bingo_number),
          product_id: p.product_id ?? null
        }))
        .filter(p => Number.isFinite(p.bingo_number) && p.bingo_number > 0);

      if (ladder.length === 0) {
        const legacyId = this.bingoSettings?.first_bingo_product_id;
        if (legacyId != null) {
          ladder = [{ bingo_number: 1, product_id: legacyId }];
        }
      }

      ladder.sort((a, b) => a.bingo_number - b.bingo_number);

      return ladder.map(p => {
        const product = p.product_id != null ? this.getProductById(p.product_id) : null;
        const name = product?.name || 'Prize';
        const unlocked = this.bingosAchieved >= p.bingo_number;
        const ord = this.ordinal(p.bingo_number);
        return {
          bingo_number: p.bingo_number,
          product_id: p.product_id,
          name,
          unlocked,
          heading: p.bingo_number === 1 ? 'Bingo Reward' : `${ord} Bingo Reward`,
          description: p.bingo_number === 1
            ? 'Complete your first bingo'
            : `Complete ${p.bingo_number} bingos`
        };
      });
    },
    latestWonPrizesForDialog() {
      const numbers = this.latestWonBingoNumbers || [];
      if (numbers.length === 0) return [];
      const byNumber = new Map(this.bingoPrizeLadder.map(p => [p.bingo_number, p]));
      return numbers
        .filter(n => byNumber.has(n))
        .map(n => {
          const ladderEntry = byNumber.get(n);
          return {
            bingo_number: n,
            name: ladderEntry.name
          };
        });
    },
    bingoLabel() {
      return this.label || 'BINGO';
    },
    bingoDealId() {
      const fromRoute = this.$route?.params?.dealId;
      return fromRoute != null ? parseInt(fromRoute, 10) : null;
    },
    categories() {
      const dealId = this.bingoDealId;
      if (dealId == null) return [];
      const deal = this.findDeal(dealId);
      const venueIds = deal?.venue_ids;
      if (!Array.isArray(venueIds) || venueIds.length === 0) return [];
      const categoriesByVenueId = this.findCategoriesByVenueIds(venueIds);
      const sortIds = deal?.pass_settings?.venue_category_sort_ids;
      // Alphabetical is the default; the stored custom order is only used in manual mode.
      const isManual = deal?.pass_settings?.category_sort_mode === 'manual';
      if (isManual && Array.isArray(sortIds) && sortIds.length > 0) {
        return applySortDisplay(categoriesByVenueId, sortIds.length, sortIds);
      }
      return applySortDisplay(categoriesByVenueId, -1, []);
    },
    hasCategories() {
      return this.categories.length > 0;
    },
    bingoFiltersForModal() {
      const { categoryId, maxDistance } = this.bingoFilters;
      return {
        categories: categoryId != null ? [categoryId] : [],
        maxDistance: maxDistance ?? null
      };
    },
    categoryModalVenuesDisabled() {
      if (this.boardMode !== 'categories') return false;
      const id = this.categoryModalData.categoryId;
      if (id == null) return false;
      return !this.getCategoryAvailability(id).isAvailable;
    },
    categoryModalNextAvailableDate() {
      const id = this.categoryModalData.categoryId;
      if (id == null) return null;
      return this.getCategoryAvailability(id).nextAvailableDate;
    }
  },
  watch: {
    lastCheckInResult: {
      handler(result) {
        if (!result) return;
        this.openBingoModalsFromCheckIn(result, this.bingoHasBlackout);
      },
      immediate: true
    },
    'bingoFilters.maxDistance'(maxDistance) {
      if (maxDistance != null && this.hasBingo) {
        const hasCoords = this.userLocation?.latitude != null && this.userLocation?.longitude != null;
        if (!hasCoords) {
          this.setUserLocation();
        }
      }
    }
  },
  mounted() {
    if (this.hasBingo) {
      this.setUserLocation();
    }
  },
  methods: {
    ...mapActions('bingo', ['setPendingBingoCell']),
    ...mapActions('userLocation', ['setUserLocation']),
    ordinal(n) {
      const v = n % 100;
      if (v >= 11 && v <= 13) return `${n}th`;
      switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
      }
    },
    onCellClick({ row, col, cell }) {
      const dealId = this.bingoDealId;
      if (!dealId) return;

      // Completed tile: go straight to the venue that marked it off, instead of the category modal.
      if (cell?.marked) {
        const completedVenueId = cell.marked_by_venue_id ?? this.getVenueIdForCell(cell);
        if (completedVenueId == null) return;
        this.$router.push({
          path: `/experiences/deal/${dealId}/venue/${completedVenueId}`,
          query: { ...this.$route.query }
        });
        return;
      }

      const isCategoryCell = cell?.type === 'category' || (Array.isArray(cell?.category_venue_ids) && cell.category_venue_ids.length > 0);
      if (isCategoryCell) {
        this.setPendingBingoCell([row, col]);
        this.$router
          .replace({
            query: mergeBingoCellQuery({ ...this.$route.query }, row, col)
          })
          .catch(() => {});
        this.categoryModalData = {
          categoryName: cell.category_name ?? 'Select a venue',
          categoryId: cell.category_id ?? null,
          categoryVenueIds: [...(cell.category_venue_ids || [])]
        };
        this.$nextTick(() => {
          this.showCategoryVenuesModal = true;
        });
        return;
      }

      const venueId = this.getVenueIdForCell(cell);
      if (venueId == null) return;

      this.setPendingBingoCell([row, col]);
      const token = window.token || this.$route.query?.t;
      const base = { ...this.$route.query };
      if (token) base.t = token;
      this.$router.push({
        path: `/experiences/deal/${dealId}/venue/${venueId}`,
        query: mergeBingoCellQuery(base, row, col)
      });
    },
    getVenueIdForCell(cell) {
      if (cell?.type === 'venue' && cell?.venue_id != null) {
        return cell.venue_id;
      }
      if (cell?.type === 'category' && cell?.category_venue_ids?.length) {
        return cell.category_venue_ids[0];
      }
      return null;
    },
    getProductForVenue(venueId, dealId) {
      const list = this.productsByVenueId(venueId, dealId) || [];
      const checkIn = list.find(
        p => String(p?.redemptions || '').toLowerCase() === 'check-in'
      );
      return checkIn || list[0] || null;
    },
    venuePassesFilter(venue, dealId) {
      if (!venue) return false;
      const { categoryId, maxDistance } = this.bingoFilters;
      if (categoryId != null) {
        const hasCategory = venue.categories?.some(c => c.id === categoryId);
        if (!hasCategory) return false;
      }
      if (maxDistance != null && this.userLocation?.latitude != null && this.userLocation?.longitude != null) {
        const venueWithCoords = {
          ...venue,
          latitude: venue?.latitude ?? venue?.lat,
          longitude: venue?.longitude ?? venue?.lng
        };
        const miles = getVenueDistance(venueWithCoords, this.userLocation);
        if (miles == null || miles > maxDistance) return false;
      }
      return true;
    },
    isCellDisabled(cell) {
      const dealId = this.bingoDealId;
      const { categoryId, maxDistance } = this.bingoFilters;
      const noFilterActive = categoryId == null && maxDistance == null;
      if (noFilterActive) return false;
      if (!cell || cell.type === 'free' || cell.marked) return false;

      if (cell.type === 'venue' && cell.venue_id != null) {
        const venue = this.findVenue(cell.venue_id, dealId);
        return !this.venuePassesFilter(venue, dealId);
      }

      if (cell.type === 'category' && Array.isArray(cell.category_venue_ids) && cell.category_venue_ids.length > 0) {
        const hasAnyMatching = cell.category_venue_ids.some(venueId => {
          const venue = this.findVenue(venueId, dealId);
          return this.venuePassesFilter(venue, dealId);
        });
        return !hasAnyMatching;
      }

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.bingo-module {
  position: relative;
  z-index: 0;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 12px;
  isolation: isolate;
}

.bingo-filters__panel {
  position: relative;
  z-index: 1;
  background-color: #fff;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px !important;
}

.bingo-filters__header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.bingo-filters__title {
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.bingo-filters__close {
  background-color: rgb(var(--v-theme-grey-lighten-3)) !important;
}

.bingo-filters__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.bingo-filters__chip-group {
  flex-wrap: wrap;
  gap: 8px;
}

.bingo-filters__chip-group :deep(.v-chip-group__content) {
  flex-wrap: wrap;
  gap: 8px;
}

.bingo-filters__chip {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  background-color: rgb(var(--v-theme-grey-lighten-4)) !important;
  color: rgba(var(--v-theme-on-surface), 0.6) !important;
  border-radius: 20px !important;
  font-size: 12px;
  font-weight: 400 !important;
  line-height: 1.25rem !important;
  padding: .25rem 1rem !important;
  min-width: 2.25rem !important;
  width: auto !important;
  text-transform: none !important;
}

.bingo-filters__chip--selected,
.bingo-filters__chip--selected.bingo-filters__chip {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
  border-radius: 20px !important;
}

.bingo-module__header {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: .75rem;
}

.bingo-filters {
    position: relative;
    z-index: 2;
    cursor: pointer;
    touch-action: manipulation;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(.4, 0, .2, 1);
    transition-duration: .15s;
    color: rgba(0, 0, 0, .6);
    font-size: .75rem;
    line-height: 1rem;
    padding-top: .25rem;
    padding-bottom: .25rem;
    padding-left: .75rem;
    padding-right: .75rem;
    background-color: #eaedf0;
    border-radius: 9999px;
}

.bingo-filters i {
    width: 12px !important;
    height: .75rem !important;
    font-size: 0.9rem !important;
}

.bingo-filters gap-1 {
    gap: .25rem;
}

.bingo-filters:hover {
    background-color: #deedf7;
}

.bingo-filters__header {
    padding: 0 !important;
    padding-bottom: 15px !important;
    border: none !important;
}

.bingo-filters__panel {
    background-color: #eaedf080 !important;
    border-color: #dae0e7 !important;
    padding: .75rem;
}

.bingo-filters__header span.bingo-filters__title {
    font-size: .75rem !important;
    line-height: 1rem !important;
    color: #1b2632 !important;
}

.bingo-filters__close {
    cursor: pointer;
    width: .875rem !important;
    height: .875rem !important;
    background: transparent;
}

.bingo-filters__close svg path {
    fill: #737f8c;
    stroke: #737f8c !important;
}

.bingo-filters__close svg {
    padding: 2px;
}

.bingo-filters__body {
    padding: 0 !important;
}

.bingo-filters__label {
    color: #737f8c !important;
    font-size: 10px !important;
    margin: 0 !important;
}

:deep(.v-slide-group__content) {
  flex-wrap: wrap;
  flex: auto;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.bingo-filters__body .bingo-filters__chip {
    cursor: pointer;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(.4, 0, .2, 1);
    transition-duration: .15s;
    font-size: 10px !important;
    padding-top: .25rem !important;
    padding-bottom: .25rem !important;
    line-height: 15px !important;
    height: auto !important;
    padding-left: .625rem !important;
    padding-right: .625rem !important;
    font-weight: 500 !important;
    margin: 0 !important;
}
</style>
